import { useEffect, useMemo, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useSelection } from "@/lib/useSelection";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Search, RefreshCw, Send, BellRing } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  sent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  logged: "bg-amber-100 text-amber-800 border-amber-200",
  failed: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function AdminEmails() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [resending, setResending] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-deliveries", { params: { limit: 500 } });
      setRows(r.data);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const templates = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => set.add(r.template_id));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (templateFilter !== "all" && r.template_id !== templateFilter) return false;
    if (!q) return true;
    const ql = q.toLowerCase();
    return (
      r.to?.toLowerCase().includes(ql) ||
      r.subject?.toLowerCase().includes(ql) ||
      r.template_id?.toLowerCase().includes(ql)
    );
  });

  const selection = useSelection(filtered);

  const resend = async (id) => {
    setResending(true);
    try {
      const r = await api.post(`/admin/email-deliveries/${id}/resend`);
      toast.success(`Resent (status: ${r.data.status})`);
      setSelected(r.data);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setResending(false);
    }
  };

  const sweepReminders = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/admin/email/send-renewal-reminders", null, {
        params: { days_before: 30 },
      });
      toast.success(`Renewal reminder sweep sent ${r.data.sent} email(s)`);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSweeping(false);
    }
  };

  return (
    <div className="animate-fade-up" data-testid="admin-emails-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Administration</div>
          <h1 className="font-heading text-3xl font-bold text-primary">Email deliveries</h1>
          <p className="text-sm text-slate-500 mt-1">
            Transactional email history for ACIS &amp; IBTHM lifecycle events. Resend at any time or trigger the renewal reminder sweep.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="emails-refresh-btn" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={sweepReminders} disabled={sweeping} data-testid="emails-sweep-btn">
            <BellRing className={`h-4 w-4 mr-2 ${sweeping ? "animate-pulse" : ""}`} /> Send renewal reminders
          </Button>
        </div>
      </div>

      <Card className="p-4 border-slate-200 mb-5">
        <div className="grid gap-3 sm:grid-cols-[1fr_180px_220px]">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="emails-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              placeholder="Filter by recipient, subject, template id"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="emails-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="logged">Logged (mock)</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={templateFilter} onValueChange={setTemplateFilter}>
            <SelectTrigger data-testid="emails-template-filter">
              <SelectValue placeholder="Template" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All templates</SelectItem>
              {templates.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {isAdmin && <BulkActionsBar collection="email_deliveries" selection={selection} onDone={load} />}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && (
                  <th className="p-3 w-10">
                    <Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="emails-select-all" />
                  </th>
                )}
                <th className="p-3">When</th>
                <th className="p-3">Recipient</th>
                <th className="p-3">Template</th>
                <th className="p-3">Subject</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="p-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="p-10 text-center">
                  <Mail className="h-9 w-9 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">No deliveries yet</div>
                </td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`email-row-${r.id}`}>
                  {isAdmin && (
                    <td className="p-3">
                      <Checkbox checked={selection.isSelected(r.id)} onCheckedChange={() => selection.toggle(r.id)} data-testid={`email-select-${r.id}`} />
                    </td>
                  )}
                  <td className="p-3 text-xs text-slate-600 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-3 text-xs text-slate-700">{r.to}</td>
                  <td className="p-3">
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700 font-mono-data text-[10px]">
                      {r.template_id}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-slate-700 max-w-xs truncate">{r.subject}</td>
                  <td className="p-3">
                    <Badge className={`border ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-700 border-slate-200"} capitalize`}>
                      {r.status}
                    </Badge>
                    {!r.configured && r.status === "logged" && (
                      <div className="text-[10px] text-slate-400 mt-1">mock provider</div>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelected(r)}
                      data-testid={`email-view-${r.id}`}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="email-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-primary">{selected?.subject}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="uppercase tracking-[0.18em] text-slate-400">Recipient</div>
                  <div className="font-medium text-slate-700">{selected.to}</div>
                </div>
                <div>
                  <div className="uppercase tracking-[0.18em] text-slate-400">Template</div>
                  <div className="font-mono-data text-slate-700">{selected.template_id}</div>
                </div>
                <div>
                  <div className="uppercase tracking-[0.18em] text-slate-400">Status</div>
                  <Badge className={`border mt-1 ${STATUS_TONE[selected.status] || ""}`}>{selected.status}</Badge>
                </div>
                <div>
                  <div className="uppercase tracking-[0.18em] text-slate-400">Provider</div>
                  <div className="text-slate-700">{selected.provider || "—"}</div>
                </div>
                {selected.error && (
                  <div className="col-span-2">
                    <div className="uppercase tracking-[0.18em] text-slate-400">Error</div>
                    <div className="text-rose-600 text-xs">{selected.error}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">Body</div>
                <div
                  className="border border-slate-200 rounded-md p-4 text-sm bg-white max-h-[300px] overflow-auto prose prose-sm"
                  dangerouslySetInnerHTML={{ __html: selected.body }}
                />
              </div>

              {selected.history?.length > 1 && (
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-2">History</div>
                  <ul className="space-y-1 text-xs text-slate-600">
                    {selected.history.map((h, i) => (
                      <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                        <span>{h.at ? new Date(h.at).toLocaleString() : "—"}</span>
                        <span className="font-mono-data">{h.status}{h.resend_by ? ` · by ${h.resend_by}` : ""}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} data-testid="email-close-btn">Close</Button>
            {selected && (
              <Button onClick={() => resend(selected.id)} disabled={resending} data-testid="email-resend-btn">
                <Send className={`h-4 w-4 mr-2 ${resending ? "animate-pulse" : ""}`} /> Resend
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
